import { z } from "zod";

/**
 * Event Registry: Single source of truth for all GA4 events
 * Organized by domain for maintainability and easy discovery
 *
 * @example
 * import { authEventsSchema, getEventDefinition } from '@/dto';
 *
 * // Type-safe event validation
 * const event = authEventsSchema.parse({ event_name: 'signup_complete', signup_method: 'email' });
 *
 * // Event introspection
 * const def = getEventDefinition('signup_complete');
 * console.log(def.description); // "User completed signup flow successfully"
 */

// ============================================================================
// AUTH EVENTS
// ============================================================================

/**
 * Auth domain events: signup, login, logout, error states
 *
 * @example
 * recordMilestone('signup_complete', { signup_method: 'email' })
 * recordMilestone('login_success', { login_method: 'github' })
 * recordStep('login_error', { error_code: 'invalid_credentials' })
 */
export const authEvents = {
  signup_complete: {
    name: "signup_complete",
    category: "milestone",
    description: "User completed signup flow successfully",
    required: ["signup_method"],
    optional: ["referred_by", "utm_source", "utm_medium", "utm_campaign"],
  },
  login_success: {
    name: "login_success",
    category: "milestone",
    description: "User logged in successfully",
    required: ["login_method"],
    optional: ["device_type"],
  },
  logout: {
    name: "logout",
    category: "milestone",
    description: "User logged out",
    required: [],
    optional: [],
  },
  login_error: {
    name: "login_error",
    category: "journey_step",
    description: "User encountered an error during login",
    required: ["error_code"],
    optional: ["error_message"],
  },
  signup_error: {
    name: "signup_error",
    category: "journey_step",
    description: "User encountered an error during signup",
    required: ["error_code"],
    optional: ["error_message"],
  },
} as const;

export const authEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("signup_complete"),
    signup_method: z.enum(["email", "github", "google", "openid"]),
    referred_by: z.string().optional(),
    utm_source: z.string().optional(),
    utm_medium: z.string().optional(),
    utm_campaign: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("login_success"),
    login_method: z.enum(["email", "github", "google", "openid", "magic_link"]),
    device_type: z.enum(["desktop", "mobile", "tablet"]).optional(),
  }),
  z.object({
    event_name: z.literal("logout"),
  }),
  z.object({
    event_name: z.literal("login_error"),
    error_code: z.string(),
    error_message: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("signup_error"),
    error_code: z.string(),
    error_message: z.string().optional(),
  }),
]);

// ============================================================================
// RESUME EVENTS
// ============================================================================

/**
 * Resume domain events: create, import, update, download, delete
 *
 * @example
 * recordStep('resume_created', { template_id: 'modern-blue' })
 * recordStep('resume_imported', { file_type: 'pdf' })
 */
export const resumeEvents = {
  resume_created: {
    name: "resume_created",
    category: "journey_step",
    description: "User created a new resume",
    required: ["template_id"],
    optional: ["source"],
  },
  resume_imported: {
    name: "resume_imported",
    category: "journey_step",
    description: "User imported a resume from a file",
    required: ["file_type"],
    optional: ["source"],
  },
  resume_updated: {
    name: "resume_updated",
    category: "journey_step",
    description: "User updated a resume section",
    required: ["section"],
    optional: ["resume_id"],
  },
  resume_downloaded: {
    name: "resume_downloaded",
    category: "journey_step",
    description: "User downloaded their resume",
    required: ["format", "resume_id"],
    optional: ["template_id"],
  },
  resume_deleted: {
    name: "resume_deleted",
    category: "journey_step",
    description: "User deleted a resume",
    required: ["resume_id"],
    optional: [],
  },
} as const;

export const resumeEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("resume_created"),
    template_id: z.string(),
    source: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("resume_imported"),
    file_type: z.enum(["pdf", "docx", "txt"]),
    source: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("resume_updated"),
    section: z.enum([
      "summary",
      "experience",
      "education",
      "skills",
      "certifications",
      "projects",
      "languages",
      "publications",
    ]),
    resume_id: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("resume_downloaded"),
    format: z.enum(["pdf", "docx"]),
    resume_id: z.string(),
    template_id: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("resume_deleted"),
    resume_id: z.string(),
  }),
]);

// ============================================================================
// JOB DISCOVERY EVENTS
// ============================================================================

/**
 * Job discovery domain events: search, view, filter, save
 *
 * @example
 * recordStep('job_search_submitted', { search_type: 'keyword', filters_applied: 2 })
 * recordStep('job_viewed', { job_id: 'li-123', board: 'linkedin' })
 */
export const jobsEvents = {
  job_search_submitted: {
    name: "job_search_submitted",
    category: "journey_step",
    description: "User submitted a job search query",
    required: ["search_type"],
    optional: ["filters_applied", "location"],
  },
  job_viewed: {
    name: "job_viewed",
    category: "journey_step",
    description: "User viewed a job posting",
    required: ["job_id", "board"],
    optional: ["source", "search_context"],
  },
  filter_applied: {
    name: "filter_applied",
    category: "journey_step",
    description: "User applied a job search filter",
    required: ["filter_type"],
    optional: ["filter_value"],
  },
  job_saved: {
    name: "job_saved",
    category: "journey_step",
    description: "User saved a job posting",
    required: ["job_id"],
    optional: ["board", "source"],
  },
} as const;

export const jobsEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("job_search_submitted"),
    search_type: z.enum(["keyword", "filters", "saved"]),
    filters_applied: z.number().optional(),
    location: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("job_viewed"),
    job_id: z.string(),
    board: z.enum(["linkedin", "indeed", "workday", "icims", "greenhouse", "lever"]),
    source: z.string().optional(),
    search_context: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("filter_applied"),
    filter_type: z.string(),
    filter_value: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("job_saved"),
    job_id: z.string(),
    board: z.enum(["linkedin", "indeed", "workday", "icims", "greenhouse", "lever"]).optional(),
    source: z.string().optional(),
  }),
]);

// ============================================================================
// APPLICATION EVENTS
// ============================================================================

/**
 * Application domain events: submit, view, response, question, error
 *
 * @example
 * recordMilestone('application_submitted', { board: 'linkedin', submission_method: 'easy_apply' })
 * recordStep('question_answered', { question_type: 'screening' })
 */
export const applicationsEvents = {
  application_submitted: {
    name: "application_submitted",
    category: "milestone",
    description: "User submitted a job application",
    required: ["board", "submission_method"],
    optional: ["job_id", "resume_used"],
  },
  application_viewed: {
    name: "application_viewed",
    category: "journey_step",
    description: "User viewed their submitted application",
    required: ["application_id"],
    optional: ["board"],
  },
  application_response_viewed: {
    name: "application_response_viewed",
    category: "journey_step",
    description: "User viewed a response to their application",
    required: ["application_id", "response_type"],
    optional: [],
  },
  question_answered: {
    name: "question_answered",
    category: "journey_step",
    description: "User answered an application question",
    required: ["question_type"],
    optional: ["question_id", "answer_source"],
  },
  error_encountered: {
    name: "error_encountered",
    category: "journey_step",
    description: "User encountered an error during application flow",
    required: ["error_code", "step"],
    optional: ["error_message"],
  },
} as const;

export const applicationsEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("application_submitted"),
    board: z.enum(["linkedin", "indeed", "workday", "icims", "greenhouse", "lever"]),
    submission_method: z.enum(["manual", "auto_apply", "easy_apply"]),
    job_id: z.string().optional(),
    resume_used: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("application_viewed"),
    application_id: z.string(),
    board: z.enum(["linkedin", "indeed", "workday", "icims", "greenhouse", "lever"]).optional(),
  }),
  z.object({
    event_name: z.literal("application_response_viewed"),
    application_id: z.string(),
    response_type: z.enum(["rejection", "interview", "offer", "other"]),
  }),
  z.object({
    event_name: z.literal("question_answered"),
    question_type: z.enum(["screening", "custom", "assessment"]),
    question_id: z.string().optional(),
    answer_source: z.enum(["manual", "ai_fallback"]).optional(),
  }),
  z.object({
    event_name: z.literal("error_encountered"),
    error_code: z.string(),
    step: z.string(),
    error_message: z.string().optional(),
  }),
]);

// ============================================================================
// AUTO-APPLY EVENTS
// ============================================================================

/**
 * Auto-apply domain events: enabled, paused, result received
 *
 * @example
 * recordMilestone('auto_apply_enabled', { tier: 'pro' })
 * recordStep('auto_apply_result_received', { result_status: 'success', board: 'linkedin' })
 */
export const autoApplyEvents = {
  auto_apply_enabled: {
    name: "auto_apply_enabled",
    category: "milestone",
    description: "User enabled auto-apply feature",
    required: [],
    optional: ["tier"],
  },
  auto_apply_paused: {
    name: "auto_apply_paused",
    category: "milestone",
    description: "User paused auto-apply feature",
    required: [],
    optional: ["reason"],
  },
  auto_apply_result_received: {
    name: "auto_apply_result_received",
    category: "journey_step",
    description: "Auto-apply completed an application attempt",
    required: ["result_status"],
    optional: ["board", "error_code"],
  },
} as const;

export const autoApplyEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("auto_apply_enabled"),
    tier: z.enum(["free", "pro", "ultimate"]).optional(),
  }),
  z.object({
    event_name: z.literal("auto_apply_paused"),
    reason: z.enum(["user_paused", "daily_limit", "hourly_limit", "error"]).optional(),
  }),
  z.object({
    event_name: z.literal("auto_apply_result_received"),
    result_status: z.enum(["success", "failed", "skipped"]),
    board: z.enum(["linkedin", "indeed", "workday", "icims", "greenhouse", "lever"]).optional(),
    error_code: z.string().optional(),
  }),
]);

// ============================================================================
// BILLING EVENTS
// ============================================================================

/**
 * Billing domain events: pricing, upgrade, subscription change, cancellation
 *
 * @example
 * recordStep('pricing_page_viewed', { current_tier: 'free' })
 * recordMilestone('subscription_changed', { new_tier: 'pro', previous_tier: 'free' })
 */
export const billingEvents = {
  pricing_page_viewed: {
    name: "pricing_page_viewed",
    category: "journey_step",
    description: "User viewed the pricing page",
    required: [],
    optional: ["current_tier", "utm_source"],
  },
  upgrade_clicked: {
    name: "upgrade_clicked",
    category: "journey_step",
    description: "User clicked on an upgrade button",
    required: ["target_tier"],
    optional: ["current_tier", "promotion_code"],
  },
  checkout_initiated: {
    name: "checkout_initiated",
    category: "journey_step",
    description:
      "User clicked a checkout CTA on the pricing page (compare with LS webhook completions to measure drop-off)",
    required: ["target_tier", "billing_cycle"],
    optional: ["locale"],
  },
  subscription_changed: {
    name: "subscription_changed",
    category: "milestone",
    description: "User upgraded or downgraded their subscription",
    required: ["new_tier", "previous_tier"],
    optional: [],
  },
  cancellation_initiated: {
    name: "cancellation_initiated",
    category: "journey_step",
    description: "User initiated subscription cancellation",
    required: [],
    optional: ["reason", "current_tier"],
  },
} as const;

export const billingEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("pricing_page_viewed"),
    current_tier: z.enum(["free", "pro", "ultimate"]).optional(),
    utm_source: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("upgrade_clicked"),
    target_tier: z.enum(["pro", "ultimate"]),
    current_tier: z.enum(["free", "pro", "ultimate"]).optional(),
    promotion_code: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("checkout_initiated"),
    target_tier: z.enum(["pro", "ultimate"]),
    billing_cycle: z.enum(["weekly", "monthly", "yearly"]),
    locale: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("subscription_changed"),
    new_tier: z.enum(["free", "pro", "ultimate"]),
    previous_tier: z.enum(["free", "pro", "ultimate"]),
  }),
  z.object({
    event_name: z.literal("cancellation_initiated"),
    reason: z.string().optional(),
    current_tier: z.enum(["pro", "ultimate"]).optional(),
  }),
]);

// ============================================================================
// ONBOARDING EVENTS
// ============================================================================

/**
 * Onboarding domain events: started, completed
 *
 * @example
 * recordMilestone('onboarding_started')
 * recordMilestone('onboarding_completed', { total_duration_seconds: 120 })
 */
export const onboardingEvents = {
  onboarding_started: {
    name: "onboarding_started",
    category: "milestone",
    description: "User started onboarding flow",
    required: [],
    optional: [],
  },
  onboarding_completed: {
    name: "onboarding_completed",
    category: "milestone",
    description: "User completed onboarding flow",
    required: [],
    optional: ["total_duration_seconds"],
  },
} as const;

export const onboardingEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("onboarding_started"),
  }),
  z.object({
    event_name: z.literal("onboarding_completed"),
    total_duration_seconds: z.number().optional(),
  }),
]);

// ============================================================================
// SETTINGS EVENTS
// ============================================================================

/**
 * Settings domain events: change setting, toggle notification, save preference, delete account
 *
 * @example
 * recordStep('setting_changed', { setting_name: 'theme', new_value: 'dark' })
 * recordMilestone('account_deleted')
 */
export const settingsEvents = {
  setting_changed: {
    name: "setting_changed",
    category: "journey_step",
    description: "User changed an application setting",
    required: ["setting_name"],
    optional: ["new_value"],
  },
  notification_toggled: {
    name: "notification_toggled",
    category: "journey_step",
    description: "User toggled notification preferences",
    required: ["notification_type", "enabled"],
    optional: [],
  },
  preference_saved: {
    name: "preference_saved",
    category: "journey_step",
    description: "User saved preferences",
    required: ["preference_type"],
    optional: [],
  },
  account_deleted: {
    name: "account_deleted",
    category: "milestone",
    description: "User deleted their account",
    required: [],
    optional: ["reason"],
  },
} as const;

export const settingsEventsSchema = z.discriminatedUnion("event_name", [
  z.object({
    event_name: z.literal("setting_changed"),
    setting_name: z.string(),
    new_value: z.string().optional(),
  }),
  z.object({
    event_name: z.literal("notification_toggled"),
    notification_type: z.string(),
    enabled: z.boolean(),
  }),
  z.object({
    event_name: z.literal("preference_saved"),
    preference_type: z.string(),
  }),
  z.object({
    event_name: z.literal("account_deleted"),
    reason: z.string().optional(),
  }),
]);

// ============================================================================
// BARREL EXPORTS
// ============================================================================

/**
 * All events organized by domain
 * Use for event discovery and introspection
 */
export const allEvents = {
  auth: authEvents,
  resume: resumeEvents,
  jobs: jobsEvents,
  applications: applicationsEvents,
  autoApply: autoApplyEvents,
  billing: billingEvents,
  onboarding: onboardingEvents,
  settings: settingsEvents,
} as const;

/**
 * Combined Zod schema for all events
 * Used for dev-time validation of event payloads
 *
 * @example
 * const result = allEventsSchema.safeParse(eventPayload);
 * if (!result.success) console.error(result.error);
 */
export const allEventsSchema = z.union([
  authEventsSchema,
  resumeEventsSchema,
  jobsEventsSchema,
  applicationsEventsSchema,
  autoApplyEventsSchema,
  billingEventsSchema,
  onboardingEventsSchema,
  settingsEventsSchema,
]);

/**
 * Helper to get event definition by name
 * Returns event metadata including category, description, and required/optional fields
 *
 * @param eventName - The event name to look up
 * @returns Event definition or null if not found
 *
 * @example
 * const def = getEventDefinition('signup_complete');
 * console.log(def?.required); // ['signup_method']
 */
export function getEventDefinition(eventName: string): {
  name: string;
  category: "milestone" | "journey_step";
  description: string;
  required: readonly string[];
  optional: readonly string[];
} | null {
  type EventDef = {
    name: string;
    category: "milestone" | "journey_step";
    description: string;
    required: readonly string[];
    optional: readonly string[];
  };

  for (const [, events] of Object.entries(allEvents)) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const event = (events as Record<string, EventDef | undefined>)[eventName];
    if (event) {
      return event;
    }
  }
  return null;
}

/**
 * Helper to check if event is a milestone event
 * Milestones are high-value user actions (signup, purchase, etc)
 *
 * @param eventName - The event name to check
 * @returns true if event is a milestone, false otherwise
 *
 * @example
 * if (isMilestoneEvent('signup_complete')) {
 *   sendToMilestoneAnalytics(event);
 * }
 */
export function isMilestoneEvent(eventName: string): boolean {
  const event = getEventDefinition(eventName);
  return event?.category === "milestone";
}

/**
 * Helper to get all events in a specific domain
 *
 * @param domain - The domain key (auth, resume, jobs, etc)
 * @returns All events in that domain
 *
 * @example
 * const authDomainEvents = getEventsByDomain('auth');
 * Object.entries(authDomainEvents).forEach(([name, def]) => {
 *   console.log(`${name}: ${def.description}`);
 * });
 */
export function getEventsByDomain(
  domain: keyof typeof allEvents,
): (typeof allEvents)[keyof typeof allEvents] {
  return allEvents[domain];
}

/**
 * Helper to get all event names in a domain
 *
 * @param domain - The domain key (auth, resume, jobs, etc)
 * @returns Array of event names in that domain
 *
 * @example
 * const authEventNames = getEventNamesInDomain('auth');
 * // ['signup_complete', 'login_success', 'logout']
 */
export function getEventNamesInDomain(domain: keyof typeof allEvents): string[] {
  const events = allEvents[domain];
  return Object.keys(events);
}

/**
 * Total count of all GA4 events defined
 */
export const TOTAL_EVENTS = Object.values(allEvents).reduce(
  (sum, domain) => sum + Object.keys(domain).length,
  0,
);
